import React, { useEffect, useState } from "react";
import { CandidateCreateOrder } from "../../../interfaces/order";
import {
  checkIfOrderExists,
  createSaveOrderSuran,
} from "../../../services/orderService";
import { useNavigate, useParams } from "react-router-dom";
import PersonalInformationForm, {
  PersonalInformation,
} from "../../../components/form-components/PersonalInformationForm";
import ConvictedOfCrimeForm, {
  ConvictedOfCrime,
} from "../../../components/form-components/ConvictedOfCrimeForm";
import LegalNamesForm, {
  LegalName,
} from "../../../components/form-components/LegalNamesForm";
import AddressForm, {
  Address,
} from "../../../components/form-components/AddressForm";
import AuthorizationForm, {
  Authorization,
} from "../../../components/form-components/AuthorizationForm";
import {
  formatDate,
  isValidBirthday,
  isValidCounty,
  isValidDate,
  isValidEmail,
  isValidPhone,
  isValidSSN,
  isValidZip,
  validateAddressHistory,
} from "../../../services/helpers";
import { getOrderFormByOrderIDType } from "../../../services/orderFormService";
import { uploadSpecialReleaseForm } from "../../../services/specialReleaseFormService";
import { SimpleClient } from "../../../interfaces/client";
import { getSimpleClientByAbbreviation } from "../../../services/clientService";
import { Card, ListGroup, Table } from "react-bootstrap";

const SuranForm = ({ clientAbbreviation }: { clientAbbreviation: string }) => {
  const { suranID } = useParams();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [unemployed, setUnemployed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [convictedOfCrimeList, setConvictedOfCrimeList] = useState<
    ConvictedOfCrime[]
  >([]);
  const [convictedOfCrimeSelection, setConvictedOfCrimeSelection] = useState<
    boolean | null
  >(null);
  const [legalNamesList, setLegalNamesList] = useState<LegalName[]>([]);
  const [additionalNameSelection, setAdditionalNameSelection] = useState<
    boolean | null
  >(null);
  const [startDate, setStartDate] = useState<Date>();
  const [residentialAddressList, setResidentialAddressList] = useState<
    Address[]
  >([
    {
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      county: "",
      fromDate: new Date(),
      toDate: new Date(),
    },
  ]);
  const [employmentAddressList, setEmploymentAddressList] = useState<Address[]>(
    [
      {
        address: "",
        zipCode: "",
        city: "",
        state: "",
        country: "",
        county: "",
        fromDate: new Date(),
        toDate: new Date(),
      },
    ],
  );
  const [personalInformation, setPersonalInformation] =
    useState<PersonalInformation>({
      firstName: "",
      lastName: "",
      middleName: "",
      email: "",
      dateOfBirth: new Date(),
      phone: "",
      race: "",
      sex: "",
      socialSecurityNumber: "",
      canadianSocialSecurityNumber: "",
    });
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [authorizationFCRA, setAuthorizationFCRA] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [hasEmploymentService] = useState(true);
  const [candidateOrder, setCandidateOrder] = useState<CandidateCreateOrder>({
    abbreviation: clientAbbreviation,
    positionCode: 1,
    candidateID: 0,
    candidateEmail: "",
    candidateFirstName: "",
    candidateLastName: "",
    suranID: suranID,
  });

  useEffect(() => {
    const loadOrder = async () => {
      try {
        //check if there is an order in pending status for this SuranID
        const suranIDString = suranID?.toString();

        if (suranIDString) {
          const existingOrder = await checkIfOrderExists(suranIDString);

          if (existingOrder) {
            //an order exists in pending state, meaning they have payment yet to complete
            console.log(
              "Order exists but payment not received. Redirecting to Payment page...",
            );
            navigate(`/suran/payment/${suranID}`);
          } else {
            //if no order with suranID exists in pending state, start a new one
            const client =
              await getSimpleClientByAbbreviation(clientAbbreviation);
            setClient(client);
            setCandidateOrder((prevOrder) => ({
              ...prevOrder,
              abbreviation: clientAbbreviation,
              suranID: suranID,
            }));

            const yearsToGoBack = 7;
            const today = new Date();
            const pastDate = new Date();
            pastDate.setFullYear(today.getFullYear() - yearsToGoBack);
            setStartDate(pastDate);
          }
        }
      } catch (error) {
        console.error("Error fetching candidate, order or orderForm:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [clientAbbreviation, suranID, navigate]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    index?: number,
    type?: string,
  ) => {
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
      value = `${value}-01`;
    }

    if (field === "convictedOfCrime" && e.target.type === "radio") {
      value = e.target.value === "true";
    }

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`${field}${type}`]: "",
    }));

    if (index !== undefined) {
      if (convictedOfCrimeList[index] && field in convictedOfCrimeList[index]) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setConvictedOfCrimeList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (legalNamesList[index] && field in legalNamesList[index]) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`${field}-${index}`];
          return updatedErrors;
        });
        setLegalNamesList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = { ...updatedList[index], [field]: value };
          return updatedList;
        });
      } else if (
        (type === "Residential" &&
          residentialAddressList[index] &&
          field in residentialAddressList[index]) ||
        (type === "Employment" &&
          employmentAddressList[index] &&
          field in employmentAddressList[index])
      ) {
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`specialReleaseForm`];
          delete updatedErrors[`${field}-${type}-${index}`];
          return updatedErrors;
        });
        if (type === "Residential") {
          setResidentialAddressList((prevList) => {
            const updatedList = [...prevList];
            updatedList[index] = { ...updatedList[index], [field]: value };
            return updatedList;
          });
        } else if (type === "Employment") {
          setEmploymentAddressList((prevList) => {
            const updatedList = [...prevList];
            updatedList[index] = { ...updatedList[index], [field]: value };
            return updatedList;
          });
        }
      }
    }

    if (field in candidateOrder) {
      const isUnemployed = value as boolean;
      setUnemployed(isUnemployed);

      if (isUnemployed) {
        employmentAddressList.forEach((_, index) =>
          handleDeleteEmploymentAddress(index),
        );
      }
    }

    if (field === "unemployed") {
      const isUnemployed = value as boolean;
      setUnemployed(isUnemployed);

      if (isUnemployed) {
        employmentAddressList.forEach((_, index) =>
          handleDeleteEmploymentAddress(index),
        );
      }
    } else if (field in personalInformation) {
      setPersonalInformation((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (field in authorization && type !== "FCRA") {
      setAuthorization((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else if (field in authorizationFCRA) {
      setAuthorizationFCRA((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleYesNoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: string,
  ) => {
    const value = e.target.value === "true";

    if (kind === "crime") {
      setConvictedOfCrimeSelection(value);

      if (!value) {
        setConvictedOfCrimeList([]);
      }

      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.convictedOfCrimeSelection;
        return updatedErrors;
      });
    } else if (kind === "name") {
      setAdditionalNameSelection(value);

      if (!value) {
        setLegalNamesList([]);
      }

      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors.additionalNameSelection;
        return updatedErrors;
      });
    }
  };

  const handleAddAnotherCrime = () => {
    setConvictedOfCrimeList((prevList) => [
      ...prevList,
      {
        convictedOfCrime: true,
        ccState: "",
        ccCounty: "",
        ccYear: "",
        comments: "",
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.convictedOfCrimeSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherLegalName = () => {
    setLegalNamesList((prevList) => [
      ...prevList,
      {
        lnFirstName: "",
        lnLastName: "",
        lnMiddleName: "",
        lnFromDate: new Date(),
        lnToDate: new Date(),
        confidential: false,
      },
    ]);

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.additionalNameSelection;
      return updatedErrors;
    });
  };

  const handleAddAnotherResidentialAddress = () => {
    setResidentialAddressList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          address: "",
          zipCode: "",
          city: "",
          state: "",
          country: "",
          county: "",
          fromDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
          toDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.residentialAddressLength;
      delete updatedErrors.addressHistory;
      return updatedErrors;
    });
  };

  const handleAddAnotherEmploymentAddress = () => {
    setEmploymentAddressList((prevList) => {
      const lastAddress =
        prevList.length > 0 ? prevList[prevList.length - 1] : null;

      return [
        ...prevList,
        {
          address: "",
          zipCode: "",
          city: "",
          state: "",
          country: "",
          county: "",
          fromDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
          toDate: lastAddress ? new Date(lastAddress.fromDate) : new Date(),
        },
      ];
    });

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors.unemployed;
      return updatedErrors;
    });
  };

  const handleDeleteConviction = (index: number) => {
    setConvictedOfCrimeList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`ccState-${index}`) ||
          key.startsWith(`ccCounty-${index}`) ||
          key.startsWith(`ccYear-${index}`) ||
          key.startsWith(`comments-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteLegalName = (index: number) => {
    setLegalNamesList((prevList) => prevList.filter((_, i) => i !== index));

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`lnFirstName-${index}`) ||
          key.startsWith(`lnLastName-${index}`) ||
          key.startsWith(`lnFromDate-${index}`) ||
          key.startsWith(`lnToDate-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteResidentialAddress = (index: number) => {
    setResidentialAddressList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`address-Residential-${index}`) ||
          key.startsWith(`zipCode-Residential-${index}`) ||
          key.startsWith(`city-Residential-${index}`) ||
          key.startsWith(`state-Residential-${index}`) ||
          key.startsWith(`county-Residential-${index}`) ||
          key.startsWith(`fromDate-Residential-${index}`) ||
          key.startsWith(`toDate-Residential-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const handleDeleteEmploymentAddress = (index: number) => {
    setEmploymentAddressList((prevList) =>
      prevList.filter((_, i) => i !== index),
    );

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      Object.keys(updatedErrors).forEach((key) => {
        if (
          key.startsWith(`address-Employment-${index}`) ||
          key.startsWith(`zipCode-Employment-${index}`) ||
          key.startsWith(`city-Employment-${index}`) ||
          key.startsWith(`state-Employment-${index}`) ||
          key.startsWith(`county-Employment-${index}`) ||
          key.startsWith(`fromDate-Employment-${index}`) ||
          key.startsWith(`toDate-Employment-${index}`)
        ) {
          delete updatedErrors[key];
        }
      });
      return updatedErrors;
    });
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    // Personal Information Validations
    if (!personalInformation?.firstName) {
      errors.firstName = "First name is required.";
    }
    if (!personalInformation?.lastName) {
      errors.lastName = "Last name is required.";
    }
    if (!personalInformation?.email) {
      errors.email = "Email address is required.";
    } else if (!isValidEmail(personalInformation.email)) {
      errors.email = "A valid email address is required.";
    }
    if (!personalInformation?.phone) {
      errors.phone = "Phone number is required.";
    } else if (!isValidPhone(personalInformation?.phone)) {
      errors.phone = "A valid phone number is required.";
    }
    if (!personalInformation?.race) {
      errors.race = "Race is required.";
    }
    if (!personalInformation?.sex) {
      errors.sex = "Sex is required.";
    }
    if (!personalInformation?.socialSecurityNumber) {
      errors.socialSecurityNumber = "Social Security Number is required.";
    } else if (!isValidSSN(personalInformation?.socialSecurityNumber)) {
      errors.socialSecurityNumber = "Enter a valid SSN";
    }
    if (!isValidDate(personalInformation?.dateOfBirth)) {
      errors.dateOfBirth = "DOB is required and must be valid.";
    } else if (!isValidBirthday(new Date(personalInformation.dateOfBirth))) {
      errors.dateOfBirth = "Please check your birth date";
    }

    // Convicted of Crime Validations
    if (convictedOfCrimeSelection === null) {
      errors.convictedOfCrimeSelection = "Please select Yes or No.";
    } else if (
      convictedOfCrimeSelection === true &&
      convictedOfCrimeList.length === 0
    ) {
      errors.convictedOfCrimeSelection =
        "You must enter at least one criminal record.";
    }

    convictedOfCrimeList.forEach((conviction, index) => {
      if (convictedOfCrimeSelection === true) {
        if (!conviction.ccState)
          errors[`ccState-${index}`] = "State is required.";
        if (!conviction.ccCounty) {
          errors[`ccCounty-${index}`] = "County is required.";
        } else if (!isValidCounty(conviction.ccCounty)) {
          errors[`ccCounty-${index}`] = "Please enter a valid county.";
        }
        if (!conviction.ccYear || !/^\d{4}$/.test(conviction.ccYear)) {
          errors[`ccYear-${index}`] = "A valid year is required.";
        }
        if (!conviction.comments)
          errors[`comments-${index}`] = "Comments are required.";
      }
    });

    // Legal Names Validations
    if (additionalNameSelection === null) {
      errors.additionalNameSelection = "Please select Yes or No.";
    } else if (
      additionalNameSelection === true &&
      legalNamesList.length === 0
    ) {
      errors.additionalNameSelection =
        "You must enter at least one legal name.";
    }

    legalNamesList.forEach((name, index) => {
      if (additionalNameSelection === true) {
        if (!name.lnFirstName)
          errors[`lnFirstName-${index}`] = "First name is required.";
        if (!name.lnLastName)
          errors[`lnLastName-${index}`] = "Last name is required.";
        if (!isValidDate(name.lnFromDate)) {
          errors[`lnFromDate-${index}`] =
            "From date is required and must be valid.";
        }
        if (!isValidDate(name.lnToDate)) {
          errors[`lnToDate-${index}`] =
            "To date is required and must be valid.";
        }
      }
    });

    // Residential Address Validations
    if (residentialAddressList.length === 0) {
      errors.residentialAddressLength =
        "You must enter at least one residential address.";
    }

    residentialAddressList.forEach((address, index) => {
      if (!address.address)
        errors[`address-Residential-${index}`] =
          "Residential address is required.";
      if (!address.zipCode) {
        errors[`zipCode-Residential-${index}`] = "ZipCode is required.";
      } else if (!isValidZip(address.zipCode)) {
        errors[`zipCode-Residential-${index}`] =
          "Please enter a valid ZipCode.";
      }
      if (!address.city)
        errors[`city-Residential-${index}`] = "City is required.";
      if (!address.state)
        errors[`state-Residential-${index}`] = "State is required.";
      if ((address.state === "GA" || address.state === "NV") && file == null) {
        errors.specialReleaseForm =
          "Please complete and upload the special release form.";
      }
      if (!address.county) {
        errors[`county-Residential-${index}`] = "County is required.";
      } else if (!isValidCounty(address.county)) {
        errors[`county-Residential-${index}`] = "Please enter a valid county.";
      }
      if (!isValidDate(address.fromDate)) {
        errors[`fromDate-Residential-${index}`] =
          "From date is required and must be valid.";
      }
      if (!isValidDate(address.toDate)) {
        errors[`toDate-Residential-${index}`] =
          "To date is required and must be valid.";
      }
    });

    // Employment Address Validations
    if (hasEmploymentService) {
      employmentAddressList.forEach((address, index) => {
        if (!address.address)
          errors[`address-Employment-${index}`] =
            "Employment address is required.";
        if (!address.zipCode) {
          errors[`zipCode-Employment-${index}`] = "ZipCode is required.";
        } else if (!isValidZip(address.zipCode)) {
          errors[`zipCode-Employment-${index}`] =
            "Please enter a valid ZipCode.";
        }
        if (!address.city)
          errors[`city-Employment-${index}`] = "City is required.";
        if (!address.state)
          errors[`state-Employment-${index}`] = "State is required.";
        if (
          (address.state === "GA" || address.state === "NV") &&
          file == null
        ) {
          errors.specialReleaseForm =
            "Please complete and upload the special release form.";
        }
        if (!address.county) {
          errors[`county-Employment-${index}`] = "County is required.";
        } else if (!isValidCounty(address.county)) {
          errors[`county-Employment-${index}`] = "Please enter a valid county.";
        }
        if (!isValidDate(address.fromDate)) {
          errors[`fromDate-Employment-${index}`] =
            "From date is required and must be valid.";
        }
        if (!isValidDate(address.toDate)) {
          errors[`toDate-Employment-${index}`] =
            "To date is required and must be valid.";
        }
      });

      if (employmentAddressList.length === 0 && unemployed === false) {
        errors.unemployed =
          "You must either add an employment address or attest that you were unemployed. ";
      }
    }

    // Authorization Validation
    if (!authorization.checked) {
      errors.checked = "You must agree before continuing.";
    }
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) {
      errors.fullName = "Full name is required.";
    }

    if (!authorizationFCRA.checked) {
      errors.checkedFCRA = "You must agree before continuing.";
    }
    if (!isValidDate(authorizationFCRA.dated)) {
      errors.datedFCRA = "Date is required and must be valid.";
    }
    if (!authorizationFCRA.fullName) {
      errors.fullNameFCRA = "Full name is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormUpload = async () => {
    try {
      setLoadingSpinner(true);
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".pdf";
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;
        setFile(file);
        setFileUploaded(true);
        setSuccessMessage("Special Release form uploaded successfully");
        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors };
          delete updatedErrors[`specialReleaseForm`];
          return updatedErrors;
        });
      };
      fileInput.click();
    } catch (error) {
      setFileUploaded(false);
      console.error("Error handling file upload:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleSave = async () => {
    setLoadingSpinner(true);
    const isValid = await validateFields();
    if (!isValid) {
      console.log("Validation failed. Please fix the errors.");
      setLoadingSpinner(false);
      const firstErrorElement = document.querySelector(".invalid-feedback");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    const addressErrors = validateAddressHistory(residentialAddressList, 7);

    if (addressErrors.length > 0) {
      const message = addressErrors.join("\n");
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        addressHistory: message,
      }));
      setTimeout(() => {
        const firstErrorElement = document.querySelector(".invalid-feedback");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      setLoadingSpinner(false);
      alert(`Your form has errors:\n\n${message}`);
      return;
    }

    setValidationErrors((prevErrors) => {
      const { addressHistory, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });

    try {
      // creates order, candidate and saves crim auth form

      const updatedCandidateOrder = {
        ...candidateOrder,
        candidateEmail: personalInformation.email,
        candidateFirstName: personalInformation.firstName,
        candidateLastName: personalInformation.lastName,
      };

      const orderResult = await createSaveOrderSuran(
        updatedCandidateOrder,
        personalInformation,
        convictedOfCrimeList,
        legalNamesList,
        residentialAddressList,
        employmentAddressList,
        authorization,
        7,
        client?.diocese ? client.diocese : "",
        hasEmploymentService,
        false,
        file ? file : undefined,
        [],
        false,
        authorizationFCRA,
      );

      //upload special release form
      const orderForm = await getOrderFormByOrderIDType(orderResult.orderID, 2);
      if (file && fileUploaded && orderForm) {
        await uploadSpecialReleaseForm(file, orderForm.orderFormID);
      }

      if (orderResult) {
        navigate(`/suran/payment/${suranID}`, {
          state: {},
        });
      }
    } catch (error) {
      console.error("Error saving criminal records authorization:", error);
      alert(
        "An error occurred while saving the form. Please try again or contact oxford document management.",
      );
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <div className="page-items-container"></div>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="text-form-header">
            Authorization to Conduct Background Investigation
          </div>
          <div className="divider"></div>
          <div>
            The items of personal information requested below are needed to
            process your background investigation. This information is intended
            solely for that purpose and will not be used in a discriminatory
            manner by the parties noted below in the making of appropriate
            business decisions. If you currently have a security freeze on your
            Equifax Credit file and the client is requiring a Social Security
            Number Trace or a Credit Bureau as part of the background
            investigation, the freeze will need to be lifted temporarily (only
            with Equifax our Credit Reporting Agency) before the Background
            Check Process can begin.
          </div>
          <br></br>
          <div>Please enter the following information.</div>
          <br></br>

          {/* Personal Information */}
          <PersonalInformationForm
            personalInformation={personalInformation}
            criminalOrderYears={7}
            validationErrors={validationErrors}
            onInputChange={handleInputChange}
            onFormUpload={handleFormUpload}
            fileUploaded={fileUploaded}
            employment={hasEmploymentService}
            suran={true}
            showSpecialRelease={true}
          />

          {/* Convicted of Crime */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              Have you ever been convicted of a crime (other than minor traffic
              offenses)?
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Yes:</label>
              <input
                className="form-check-input"
                type="radio"
                name="convictedOfCrime"
                value="true"
                checked={convictedOfCrimeSelection === true}
                onChange={(e) => handleYesNoChange(e, "crime")}
              />
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">No:</label>
              <input
                className="form-check-input"
                type="radio"
                name="convictedOfCrime"
                value="false"
                checked={convictedOfCrimeSelection === false}
                onChange={(e) => handleYesNoChange(e, "crime")}
              />
            </div>
            {validationErrors.convictedOfCrimeSelection && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.convictedOfCrimeSelection}
              </div>
            )}
            <div style={{ flex: "0 0 70%" }}></div>

            {convictedOfCrimeSelection && (
              <div style={{ flex: "0 0 100%" }}>
                {convictedOfCrimeList.map((convictedOfCrime, index) => (
                  <ConvictedOfCrimeForm
                    key={index}
                    convictedOfCrime={convictedOfCrime}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteConviction(index)}
                    index={index}
                  />
                ))}
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className="oxbtn btn-purple"
                    onClick={() => handleAddAnotherCrime()}
                  >
                    Add An Entry
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Legal Names */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              Have you ever used other legal names, including maiden name?
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Yes:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="true"
                checked={additionalNameSelection === true}
                onChange={(e) => handleYesNoChange(e, "name")}
              />
            </div>
            <div className="field-long" style={{ flex: "0 0 10%" }}>
              <label className="field-label">No:</label>
              <input
                className="form-check-input"
                type="radio"
                name="additionalName"
                value="false"
                checked={additionalNameSelection === false}
                onChange={(e) => handleYesNoChange(e, "name")}
              />
            </div>
            {validationErrors.additionalNameSelection && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.additionalNameSelection}
              </div>
            )}
            <div style={{ flex: "0 0 70%" }}></div>

            {additionalNameSelection && (
              <div style={{ flex: "0 0 100%" }}>
                <div className="text-body" style={{ flex: "0 0 100%" }}>
                  Other legal names you have used, including maiden name and the
                  year(s) your name(s) changed:
                </div>
                <br></br>
                {legalNamesList.map((legalName, index) => (
                  <LegalNamesForm
                    key={index}
                    legalName={legalName}
                    validationErrors={validationErrors}
                    onInputChange={handleInputChange}
                    onDelete={() => handleDeleteLegalName(index)}
                    index={index}
                  />
                ))}
                <br></br>
                <div className="btn-center" style={{ flex: "0 0 100%" }}>
                  <button
                    className="oxbtn btn-purple"
                    onClick={() => handleAddAnotherLegalName()}
                  >
                    Add A Previous Legal Name
                    <i className="bi bi-plus-lg btn-icon"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Residential Addresses */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              <div className="text-container-header">Residential Addresses</div>
              <br></br>
              Please list all of your residential addresses for the past 7
              years, starting with your present address. (Include all addresses
              where you physically resided in the last 7 years including college
              and seasonal property addresses.)
              <ul>
                <li>
                  If any of your addresses are in Canada, please select "CANADA"
                  from the state drop-down list.
                </li>
                <li>
                  If any of your addresses are outside of the US and Canada,
                  please fill in your address but select "INT" from the state
                  drop-down list.
                </li>
                <li>
                  * You need to go back to <b>{formatDate(startDate, "us")}</b>{" "}
                  with your residential history. *
                </li>
              </ul>
            </div>
            {validationErrors.residentialAddressLength && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.residentialAddressLength}
              </div>
            )}
            {validationErrors.addressHistory && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {validationErrors.addressHistory}
              </div>
            )}
            {residentialAddressList.map((address, index) => (
              <AddressForm
                key={index}
                address={address}
                validationErrors={validationErrors}
                onInputChange={handleInputChange}
                onDelete={() => handleDeleteResidentialAddress(index)}
                index={index}
                type="Residential"
              />
            ))}
            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className="oxbtn btn-purple"
                onClick={handleAddAnotherResidentialAddress}
              >
                Add An Address
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
          </div>

          {/* Employment Addresses */}
          {hasEmploymentService && (
            <div className="data-container">
              <div className="text-body" style={{ flex: "0 0 100%" }}>
                <div className="text-container-header">
                  Employment Addresses
                </div>
                <br></br>
                 Please list all of your employment addresses for the past 7
                years. If you were not working during any point over the
                previous 7 years, simply skip over those periods of time.
                <ul>
                  <li>
                    If any of your addresses are in Canada please select
                    "CANADA" from the state drop-down list.
                  </li>
                  <li>
                    If any of your addresses are outside of the US and Canada,
                    please fill in your address but select "INT" from the state
                    drop-down list.
                  </li>
                  <li>
                    * You need to go back to{" "}
                    <b>{formatDate(startDate, "us")}</b> with your employment
                    history. *
                  </li>
                </ul>
              </div>
              <div className="field-long" style={{ flex: "0 0 100%" }}>
                <label className="field-label" style={{ textWrap: "wrap" }}>
                  I have been unemployed for this time period:
                </label>
                <div className="field-input" style={{ flex: "0 0 3%" }}>
                  <input
                    className={`form-check-input ${
                      validationErrors.unemployed ? "is-invalid" : ""
                    }`}
                    type="checkbox"
                    checked={unemployed}
                    onChange={(e) => handleInputChange(e, "unemployed")}
                  />
                </div>
                <div>
                  {validationErrors.unemployed && (
                    <div
                      className="invalid-feedback"
                      style={{ display: "block" }}
                    >
                      {validationErrors.unemployed}
                    </div>
                  )}
                </div>
              </div>
              {employmentAddressList.map((address, index) => (
                <AddressForm
                  key={index}
                  address={address}
                  validationErrors={validationErrors}
                  onInputChange={handleInputChange}
                  onDelete={() => handleDeleteEmploymentAddress(index)}
                  index={index}
                  type="Employment"
                />
              ))}
              <div className="btn-center" style={{ flex: "0 0 100%" }}>
                <button
                  className={`oxbtn btn-purple ${unemployed ? "disabled" : ""}`}
                  onClick={handleAddAnotherEmploymentAddress}
                  disabled={unemployed}
                >
                  Add An Address
                  <i className="bi bi-plus-lg btn-icon"></i>
                </button>
              </div>
            </div>
          )}

          {/* Authorization */}
          <div className="data-container">
            <div className="text-body" style={{ flex: "0 0 100%" }}>
              I authorize {client?.diocese} and/or Oxford Document Management
              and their agents to investigate my background in connection with
              my application for employment, appointment or a volunteer
              position. I understand that this may include investigative
              inquiries related to my criminal (at the county, state and federal
              jurisdictions), civil, SSN verification and education
              verification. I hereby release and hold harmless all persons,
              agencies or corporations furnishing such information from
              liability and responsibility. A photo static/fax/electronic copy
              of this document can be substituted for the original. This
              document shall be valid for a period of one (1) year from the date
              of my signature.
            </div>
            <AuthorizationForm
              authText={
                "I attest that the answers I have given are true to the best of my knowledge and hereby consent to the release of the above-described records."
              }
              authorization={authorization}
              validationErrors={validationErrors}
              onInputChange={handleInputChange}
              type={""}
            />
          </div>

          <div className="data-container">
            <div className="text-body" style={{ textAlign: "center" }}>
              <b>
                BACKGROUND INVESTIGATIVE REPORTS DISCLOSURE & AUTHORIZATION
                REGARDING PROCUREMENT OF AN INVESTIGATIVE CONSUMER REPORT
              </b>
            </div>
            <div className="text-container-suran">
              <div>
                In connection with your application for employment or when
                deciding whether to modify or continue your employment (if you
                are hired), we may obtain and use an "investigative consumer
                report" about you from a "consumer reporting agency." A
                "consumer reporting agency" is a person or business which, for
                monetary fees, dues, or on a cooperative nonprofit basis,
                regularly assembles or evaluates consumer credit information or
                other information on consumers for the purpose of furnishing
                "investigative consumer reports" to third parties.
              </div>
              <br></br>
              <div>
                This report may include information as to your character,
                general reputation, personal characteristics and mode of living.
                This information may be obtained by contacting your previous
                employers and/or references supplied by you or others.
              </div>
              <br></br>
              <div>
                In the event that information from the report is utilized in
                whole or in part in making an adverse decision with regard to
                your employment. before making the adverse decision, we will
                provide you with a copy of the investigative consumer report and
                a description in writing of your rights under the law.
              </div>
              <br></br>
              <div>
                Please be advised that you have the right to request, in
                writing, within a reasonable time, that we make a complete and
                accurate disclosure of the nature and scope of the information
                requested. Such disclosure will be made to you within 5 days of
                the date on which we receive the request from you or within 5
                days of the time the report was first requested.
              </div>
              <br></br>
              <div className="text-body">
                <b>
                  The Fair Credit Reporting Act gives you specific rights in
                  dealing with consumer reporting agencies:
                </b>
              </div>
              <br></br>
              <p>
                The federal Fair Credit Reporting Act (FCRA) promotes the
                accuracy, fairness, and privacy of information in the files of
                consumer reporting agencies. There are many types of consumer
                reporting agencies, including credit bureaus and specialty
                agencies (such as agencies that sell information about check
                writing histories, medical records, and rental history records).
                Here is a summary of your major rights under the FCRA.
              </p>
              <p>
                <strong>
                  For more information, including information about additional
                  rights, go to{" "}
                  <a
                    href="http://www.consumerfinance.gov/learnmore"
                    style={{ color: "#0000FF" }}
                  >
                    www.consumerfinance.gov/learnmore
                  </a>{" "}
                  or write to: Consumer Financial Protection Bureau, 1700 G
                  Street N.W., Washington, DC 20552.
                </strong>
              </p>
              <div className="text-body">Your Rights Under the FCRA</div>
              <Card>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>
                      1. You must be told if information in your file has been
                      used against you.
                    </strong>
                    Anyone who uses a credit report or another type of consumer
                    report to deny your application for credit, insurance, or
                    employment must tell you, and must give you the name,
                    address, and phone number of the agency that provided the
                    information.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      2. You have the right to know what is in your file.
                    </strong>
                    You may request and obtain all the information about you in
                    the files of a consumer reporting agency (your “file
                    disclosure”). You will be required to provide proper
                    identification, which may include your Social Security
                    number.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      3. You have the right to ask for a credit score.
                    </strong>
                    Credit scores are numerical summaries of your
                    credit-worthiness based on information from credit bureaus.
                    You may request a credit score from consumer reporting
                    agencies that create scores or distribute scores used in
                    residential real property loans, but you will have to pay
                    for it.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      4. You have the right to dispute incomplete or inaccurate
                      information.
                    </strong>
                    If you identify information in your file that is incomplete
                    or inaccurate, and report it to the consumer reporting
                    agency, the agency must investigate unless your dispute is
                    frivolous.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      5. Consumer reporting agencies must correct or delete
                      inaccurate, incomplete, or unverifiable information.
                    </strong>
                    Inaccurate, incomplete or unverifiable information must be
                    removed or corrected, usually within 30 days. However, a
                    consumer reporting agency may continue to report information
                    it has verified as accurate.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      6. Consumer reporting agencies may not report outdated
                      negative information.
                    </strong>
                    In most cases, a consumer reporting agency may not report
                    negative information that is more than seven years old, or
                    bankruptcies that are more than 10 years old.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>7. Access to your file is limited.</strong>A
                    consumer reporting agency may provide information about you
                    only to people with a valid need — usually to consider an
                    application with a creditor, insurer, employer, landlord, or
                    other business.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      8. You must give your consent for reports to be provided
                      to employers.
                    </strong>
                    A consumer reporting agency may not give out information
                    about you to your employer, or a potential employer, without
                    your written consent.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      9. You may limit "prescreened" offers of credit and
                      insurance you get based on information in your credit
                      report.
                    </strong>
                    Unsolicited "prescreened" offers for credit and insurance
                    must include a toll-free phone number you can call if you
                    choose to remove your name and address from the lists these
                    offers are based on.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>10. You may seek damages from violators.</strong>
                    If a consumer reporting agency, or, in some cases, a user of
                    consumer reports or a furnisher of information to a consumer
                    reporting agency violates the FCRA, you may be able to sue
                    in state or federal court.
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>
                      11. Identity theft victims and active duty military
                      personnel have additional rights.
                    </strong>
                    For more information, visit{" "}
                    <a
                      href="http://www.consumerfinance.gov/learnmore"
                      style={{ color: "#0000FF" }}
                    >
                      www.consumerfinance.gov/learnmore
                    </a>
                    .
                  </ListGroup.Item>
                </ListGroup>
              </Card>
              <br></br>
              <p>
                <strong>
                  States may enforce the FCRA, and many states have their own
                  consumer reporting laws. In some cases, you may have more
                  rights under state law. For more information, contact your
                  state or local consumer protection agency or your state
                  Attorney General.
                </strong>
              </p>

              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>Type of Business</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      1. Banks, savings associations, and credit unions with
                      total assets of over $10 billion and their affiliates
                    </td>
                    <td>
                      <strong>Consumer Financial Protection Bureau</strong>
                      <br />
                      1700 G Street NW
                      <br />
                      Washington, DC 20552
                      <br />
                      <strong>Federal Trade Commission:</strong>
                      <br />
                      Consumer Response Center – FCRA
                      <br />
                      Washington, DC 20580
                      <br />
                      (877) 382-4357
                    </td>
                  </tr>
                  <tr>
                    <td>
                      2. National banks, federal savings associations and
                      federal branches and federal agencies of foreign banks
                    </td>
                    <td>
                      <strong>Office of the Comptroller of the Currency</strong>
                      <br />
                      Customer Assistance Group
                      <br />
                      1301 McKinney Street, Suite 3450
                      <br />
                      Houston, TX 77010-9050
                      <br />
                      <strong>Federal Reserve Consumer Help Center</strong>
                      <br />
                      PO Box 1200
                      <br />
                      Minneapolis, MN 55480
                      <br />
                      <strong>FDIC Consumer Response Center</strong>
                      <br />
                      1100 Walnut St., Box #11
                      <br />
                      Kansas City, MO 64106
                    </td>
                  </tr>
                  <tr>
                    <td>3. Air carriers</td>
                    <td>
                      Asst. General Counsel for Aviation Enforcement &amp;
                      Proceedings
                      <br />
                      Aviation Consumer Protection Division
                      <br />
                      Department of Transportation
                      <br />
                      1200 New Jersey Avenue, S.E.
                      <br />
                      Washington, DC 20590
                    </td>
                  </tr>
                  <tr>
                    <td>
                      4. Creditors Subject to Surface Transportation Board
                    </td>
                    <td>
                      Office of Proceedings, Surface Transportation Board
                      <br />
                      Department of Transportation
                      <br />
                      395 E Street, S.W.
                      <br />
                      Washington, DC 20423
                    </td>
                  </tr>
                  <tr>
                    <td>
                      5. Creditors Subject to Packers and Stockyards Act, 1921
                    </td>
                    <td>
                      Nearest Packers and Stockyards Administration area
                      Supervisor
                    </td>
                  </tr>
                  <tr>
                    <td>6. Small Business Investment Companies</td>
                    <td>
                      Associate Deputy Administrator for Capital Access
                      <br />
                      United States Small Business Administration
                      <br />
                      409 Third Street, SW, 8th Floor
                      <br />
                      Washington, DC 20416
                    </td>
                  </tr>
                  <tr>
                    <td>7. Brokers and Dealers</td>
                    <td>
                      Securities and Exchange Commission
                      <br />
                      100 F Street, N.E. Washington, DC 20549
                    </td>
                  </tr>
                  <tr>
                    <td>
                      8. Federal Land Banks, Federal Land Bank Associations,
                      Federal Intermediate Credit Banks and Production Credit
                      Associations
                    </td>
                    <td>
                      Farm Credit Administration
                      <br />
                      1501 Farm Credit Drive
                      <br />
                      McLean, VA 22102-5090
                    </td>
                  </tr>
                  <tr>
                    <td>
                      9. Retailers, Finance Companies, and All Other Creditors
                      Not Listed Above
                    </td>
                    <td>
                      FTC Regional Office for region in which the creditor
                      operates or Federal Trade Commission:
                      <br />
                      Consumer Response Center - FCRA
                      <br />
                      Washington, DC 20580 (877) 382-4357
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <AuthorizationForm
              authText="Yes, I have read and understand the FCRA Disclosure/Authorization and my rights under the Fair Credit Reporting Act. By checking this box, I am providing my electronic signature to this authorization form and agree that such signature is the legally-binding equivalent to my handwritten signature."
              authorization={authorizationFCRA}
              validationErrors={validationErrors}
              onInputChange={handleInputChange}
              type={"FCRA"}
            />
          </div>

          <div className="btn-center">
            <button className="oxbtn btn-green" onClick={() => handleSave()}>
              Save and Continue
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};
export default SuranForm;
